import React, { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { submitLead } from "@/lib/api";

/**
 * LeadForm – reusable form for consultation / site-host / grant / contact.
 * Props: type, fields (array of {name,label,type,required,placeholder,as,options,colSpan}), submitLabel, testIdPrefix.
 */
export default function LeadForm({ type, fields, submitLabel = "Send Request", testIdPrefix = "leadform" }) {
    const initialState = Object.fromEntries(fields.map((f) => [f.name, ""]));
    const [values, setValues] = useState(initialState);
    const [status, setStatus] = useState({ state: "idle", message: "" });

    const handleChange = (name, value) => setValues((v) => ({ ...v, [name]: value }));

    const submit = async (e) => {
        e.preventDefault();
        setStatus({ state: "loading", message: "" });
        try {
            const payload = { ...values };
            // Coerce numbers
            fields.forEach((f) => {
                if (f.type === "number" && payload[f.name] !== "" && payload[f.name] != null) {
                    payload[f.name] = Number(payload[f.name]);
                }
                if (payload[f.name] === "") delete payload[f.name];
            });
            await submitLead(type, payload);
            setStatus({ state: "success", message: "Thank you. Our team will reach out within one business day." });
            setValues(initialState);
        } catch (err) {
            const detail = err?.response?.data?.detail || err?.message || "Something went wrong. Please try again.";
            setStatus({ state: "error", message: typeof detail === "string" ? detail : "Submission failed. Please try again." });
        }
    };

    return (
        <form onSubmit={submit} data-testid={`${testIdPrefix}-form`} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {fields.map((f) => (
                    <div key={f.name} className={f.colSpan === 2 ? "md:col-span-2" : ""}>
                        <label htmlFor={`${testIdPrefix}-${f.name}`} className="block text-[11px] uppercase tracking-[0.2em] text-slate-400 mb-2">
                            {f.label}{f.required && <span className="text-electric-400 ml-1">*</span>}
                        </label>
                        {f.as === "textarea" ? (
                            <textarea
                                id={`${testIdPrefix}-${f.name}`}
                                data-testid={`${testIdPrefix}-${f.name}`}
                                value={values[f.name]}
                                onChange={(e) => handleChange(f.name, e.target.value)}
                                required={f.required}
                                placeholder={f.placeholder}
                                rows={4}
                                className="w-full bg-ink-800 border border-white/10 text-white focus:border-electric-400 focus:ring-1 focus:ring-electric-400 px-4 py-3 placeholder-slate-600 transition-colors outline-none"
                            />
                        ) : f.as === "select" ? (
                            <select
                                id={`${testIdPrefix}-${f.name}`}
                                data-testid={`${testIdPrefix}-${f.name}`}
                                value={values[f.name]}
                                onChange={(e) => handleChange(f.name, e.target.value)}
                                required={f.required}
                                className="w-full bg-ink-800 border border-white/10 text-white focus:border-electric-400 focus:ring-1 focus:ring-electric-400 px-4 py-3 transition-colors outline-none"
                            >
                                <option value="">{f.placeholder || "Select…"}</option>
                                {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                        ) : (
                            <input
                                id={`${testIdPrefix}-${f.name}`}
                                data-testid={`${testIdPrefix}-${f.name}`}
                                type={f.type || "text"}
                                value={values[f.name]}
                                onChange={(e) => handleChange(f.name, e.target.value)}
                                required={f.required}
                                placeholder={f.placeholder}
                                className="w-full bg-ink-800 border border-white/10 text-white focus:border-electric-400 focus:ring-1 focus:ring-electric-400 px-4 py-3 placeholder-slate-600 transition-colors outline-none"
                            />
                        )}
                    </div>
                ))}
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
                <p className="text-xs text-slate-500 max-w-md leading-relaxed">By submitting, you consent to M-Class EV Charging contacting you about your inquiry. We never share your information.</p>
                <button
                    type="submit"
                    disabled={status.state === "loading"}
                    data-testid={`${testIdPrefix}-submit`}
                    className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    {status.state === "loading" ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending</>) : submitLabel}
                </button>
            </div>

            {status.state === "success" && (
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    data-testid={`${testIdPrefix}-success`}
                    className="flex items-start gap-3 border border-electric-400/40 bg-electric-400/5 p-4 text-sm text-electric-50">
                    <CheckCircle2 className="w-5 h-5 text-electric-400 shrink-0 mt-0.5" />
                    <span>{status.message}</span>
                </motion.div>
            )}
            {status.state === "error" && (
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    data-testid={`${testIdPrefix}-error`}
                    className="flex items-start gap-3 border border-red-500/40 bg-red-500/5 p-4 text-sm text-red-200">
                    <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                    <span>{status.message}</span>
                </motion.div>
            )}
        </form>
    );
}
